import type {
    IPlugin,
    IProcessTelemetryContext,
    ITelemetryItem,
    IExtendedConfiguration,
} from '@microsoft/1ds-analytics-js';
import { trace } from 'owa-trace';

// 1DS 4.4.2 compares eventBlob.length against these unload and normal record limits.
const EVENT_LIMITS = [65_000, 2_000_000];
const LEGACY_ECS_COLUMNS = [
    'EcsConfigIds',
    'EcsDefaultConfigIds',
    'EcsRolloutIds',
    'EcsConfigAliases',
    'EcsPairedStages',
    'EcsETag',
];
const encoder = new TextEncoder();

export function getEcsScorecardLegacySizeLowerBound(item: ITelemetryItem): number {
    let length = 0;
    for (const key of LEGACY_ECS_COLUMNS) {
        const value: unknown = item.data?.[key];
        if (typeof value === 'string' && value.length > 0) {
            // These flat strings survive the pinned serializer verbatim after JSON escaping.
            // Exclude all other fields, wrappers and commas from this UTF-16 lower bound.
            length += JSON.stringify(key).length + 1 + JSON.stringify(value).length;
        }
    }
    return length;
}

function metadataBound(value: unknown): Record<string, unknown> | undefined {
    if (
        typeof value !== 'object' ||
        value === null ||
        Object.getPrototypeOf(value) !== Object.prototype ||
        Object.getOwnPropertyDescriptor(value, 'value') !== undefined
    ) {
        return undefined;
    }
    const result: Record<string, unknown> = {};
    for (const key of Object.keys(value)) {
        const descriptor = Object.getOwnPropertyDescriptor(value, key);
        // Prototype keys and accessors have serializer-dependent behavior.
        if (
            key === '__proto__' ||
            key === 'constructor' ||
            key === 'prototype' ||
            !descriptor ||
            !('value' in descriptor)
        ) {
            return undefined;
        }
        const field: unknown = descriptor.value;
        if (
            field === undefined ||
            field === null ||
            typeof field === 'string' ||
            typeof field === 'boolean' ||
            (typeof field === 'number' && Number.isFinite(field))
        ) {
            if (typeof field === 'number') {
                result[key] = { t: 6 };
            }
        } else {
            const nested = metadataBound(field);
            if (!nested) {
                return undefined;
            }
            if (Object.keys(nested).length > 0) {
                result[key] = { f: nested };
            }
        }
    }
    return result;
}

export function getEcsScorecardEventSizeBound(
    item: ITelemetryItem,
    configuration?: IExtendedConfiguration
): number | undefined {
    const channel =
        configuration?.extensionConfig?.PostChannel ?? configuration?.channelConfiguration;
    const compoundKeys =
        channel?.enableCompoundKey !== undefined
            ? channel.enableCompoundKey
            : configuration?.enableCompoundKey;
    if (
        !configuration ||
        compoundKeys ||
        channel?.valueSanitizer ||
        channel?.stringifyObjects ||
        (channel?.requestLimit && Object.keys(channel.requestLimit).length > 0)
    ) {
        return undefined;
    }
    const data = item.data ?? {};
    const baseData = item.baseData ?? {};
    const dataMetadata = metadataBound(data);
    const baseMetadata = metadataBound(baseData);
    if (!dataMetadata || !baseMetadata || (item.ext && !metadataBound(item.ext))) {
        return undefined;
    }
    // With compound keys disabled, dotted names (including numeric metadata keys) stay literal.
    // For these plain scalar records it adds metadata only for numbers (type 6).
    // Count that metadata separately, without duplicating baseData or dropped root fields.
    const metadata = { ...dataMetadata };
    if (Object.keys(baseMetadata).length > 0) {
        metadata.baseData = { f: baseMetadata };
    }
    return encoder.encode(
        JSON.stringify({
            name: item.name,
            time: item.time,
            ver: item.ver,
            iKey: `o:${(item.iKey ?? '').split('-')[0]}`,
            ext: {
                ...item.ext,
            },
            additionalMetadataUpperBound:
                Object.keys(metadata).length > 0 ? { f: metadata } : undefined,
            data: { baseType: item.baseType, baseData, ...data },
        })
    ).length;
}

export class EcsScorecardSizePlugin implements IPlugin {
    readonly identifier = 'OWAEcsScorecardSizePlugin';
    // After privacy (499), but still a core extension before ChannelController (500).
    readonly priority = 499.5;

    initialize(): void {}

    processTelemetry(item: ITelemetryItem, context?: IProcessTelemetryContext): void {
        if (
            item.name === 'client_ecs_config_state' &&
            item.data?.EventName === 'EcsConfigStateStamping' &&
            item.data.EcsScorecardFlights !== undefined
        ) {
            let upper: number | undefined;
            let lower = 0;
            try {
                upper = getEcsScorecardEventSizeBound(item, context?.getCfg());
                lower = getEcsScorecardLegacySizeLowerBound(item);
            } catch {
                // Unsupported serialization shapes must not jeopardize the legacy payload.
                upper = undefined;
            }
            if (
                upper === undefined ||
                EVENT_LIMITS.some(limit => upper > limit && lower <= limit)
            ) {
                delete item.data.EcsScorecardFlights;
                trace.warn(
                    'Compact ECS column omitted because its serialization could cross an SDK event limit.',
                    'analytics'
                );
            }
        }
        context?.processNext(item);
    }
}
